import React, { useState, useEffect, useCallback } from 'react';
import {
  NavBar,
  NavTabs,
  NavTab,
  SectionWrapper,
  // You can add a styled toggle button if desired
} from './About.styles';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

import WhoWeAre from './WhoWeAre/WhoWeAre';
import MissionVision from './MissionVision/MissionVision';
import Leadership from './Leadership/Leadership';
import CommunityImpact from './CommunityImpact/CommunityImpact';
import CoreValues from './CoreValues/CoreValues';
import JoinUs from './JoinUs/JoinUs';
import { PageContainer } from '../../components/common/PageContainer/PageContainer.styles';

const About: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('');
  const [navVisible, setNavVisible] = useState<boolean>(true);

  // Scroll to a specific section
  const scrollToSection = useCallback((sectionId: string) => {
    const targetSection = document.querySelector(sectionId);
    if (targetSection) {
      const yOffset = -160; // Adjust for fixed header
      const yPosition =
        targetSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({
        top: yPosition,
        behavior: 'smooth',
      });
    }
  }, []);

  useEffect(() => {
    // Scroll to section if hash exists in URL
    const hash = window.location.hash;
    if (hash) {
      setActiveTab(hash);
      scrollToSection(hash);
    }

    // Handle scroll to update active tab
    const handleScroll = () => {
      const sections = [
        '#who-we-are',
        '#mission-vision',
        '#leadership',
        '#impact',
        '#core-values',
        '#join-us',
      ];
      for (const section of sections) {
        const el = document.querySelector(section);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveTab(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrollToSection]);

  // Handle tab click: scroll, set active tab and collapse nav
  const handleTabClick = (e: React.MouseEvent<HTMLAnchorElement>, tabId: string) => {
    e.preventDefault();
    setActiveTab(tabId);
    scrollToSection(tabId);
    setNavVisible(false); // collapse nav on tab selection
  };

  // Toggle nav visibility
  const toggleNav = () => setNavVisible((prev) => !prev);

  return (
    <PageContainer>
      <NavBar>
        {/* Toggle Button for the Nav Tabs */}
        <div
          onClick={toggleNav}
          style={{
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0.5rem',
          }}
        >
          {navVisible ? (
            <FaChevronUp size={20} color="#039BE5" />
          ) : (
            <FaChevronDown size={20} color="#039BE5" />
          )}
        </div>
        {/* Conditionally render the navigation tabs */}
        {navVisible && (
          <NavTabs style={{ transition: 'all 0.3s ease' }}>
            {[
              { id: '#who-we-are', label: 'Who We Are' },
              { id: '#mission-vision', label: 'Mission & Vision' },
              { id: '#leadership', label: 'Our Leadership' },
              { id: '#impact', label: 'Community Impact' },
              { id: '#core-values', label: 'Core Values' },
              { id: '#join-us', label: 'Join Us' },
            ].map((tab) => (
              <NavTab
                key={tab.id}
                href={tab.id}
                onClick={(e) => handleTabClick(e, tab.id)}
                $active={activeTab === tab.id}
              >
                {tab.label}
              </NavTab>
            ))}
          </NavTabs>
        )}
      </NavBar>

      <SectionWrapper id="who-we-are">
        <WhoWeAre />
      </SectionWrapper>

      <SectionWrapper id="mission-vision">
        <MissionVision />
      </SectionWrapper>

      <SectionWrapper id="leadership">
        <Leadership />
      </SectionWrapper>

      <SectionWrapper id="impact">
        <CommunityImpact />
      </SectionWrapper>

      <SectionWrapper id="core-values">
        <CoreValues />
      </SectionWrapper>

      <SectionWrapper id="join-us">
        <JoinUs />
      </SectionWrapper>
    </PageContainer>
  );
};

export default About;
